import React, { useCallback, useEffect, useRef, useState } from "react";
// @ts-ignore
import Select from "react-select";
import constants from "../../../tools/constants";
import {
  tableTheme,
  tableToolbarControlHeight,
  tableToolbarContentHeight,
  tableToolbarGroupCornerRight,
} from "../../common/tableTheme";

// ─── API ──────────────────────────────────────────────────────────────────────

type LabelOption = { value: string; label: string };

export const MAX_PREVIEW_IMAGES = 20;

export interface PreviewResult {
  total: number;
  images: Array<{ id: string; name: string; url: string; labels: any }>;
  countByLabel?: Record<string, number>;
}

export const buildFakePreviewImages = (
  count: number,
): PreviewResult["images"] =>
  Array.from({ length: count }, (_, index) => ({
    id: `preview-${index}-${Math.random()
      .toString(36)
      .slice(2, 9)}`,
    name: `image-${index + 1}.png`,
    url: `https://picsum.photos/seed/preview-${index}/200/200`,
    labels: null,
  }));

export const fetchImagesCountByLabels = async (
  appFetch: (url: string, opts?: any) => Promise<any>,
  labelFilter: string[],
  sourceProjectIds: string[],
  clientId?: string,
  labelingType?: string,
): Promise<PreviewResult> => {
  const result: PreviewResult = await appFetch(
    `${constants.paths.PREVIEW_IMAGES_PATH}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sourceProjectIds,
        labelFilter,
        clientId,
        labelingType,
      }),
    },
  );

  const r = {
    ...result,
    images: buildFakePreviewImages(MAX_PREVIEW_IMAGES),
  };
  // return r;
  return result;
};

export const fetchAvailableLabels = async (
  appFetch: (url: string, opts?: any) => Promise<any>,
  clientId?: string,
  labelingType?: string,
): Promise<LabelOption[]> => {
  const params = new URLSearchParams();
  if (clientId) params.set("clientId", clientId);
  if (labelingType) params.set("labelingType", labelingType);
  const query = params.toString();
  const result: string[] = await appFetch(
    `/api/projects/available-labels${query ? `?${query}` : ""}`,
  );
  return Array.isArray(result)
    ? result.map((l) => ({ value: l, label: l }))
    : [];
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const defaultBorder = "1px solid rgba(72,72,72,.2)";
const hoverBorder = "1px solid rgba(72,72,72,.4)";
const focusBorder = "1px solid #80bdff";
const focusShadow = "0 0 0 0.2rem rgba(0,123,255,.25)";
const MENU_PORTAL_Z_INDEX = 9999;

const menuPortalStyle = (provided: any) => ({
  ...provided,
  zIndex: MENU_PORTAL_Z_INDEX,
});

const createSelectStyles = (withFocusStyle: boolean): any => ({
  control: (base: any, state: any) => ({
    ...base,
    borderRadius: 4,
    backgroundColor: "#fff",
    border: withFocusStyle && state.isFocused ? focusBorder : defaultBorder,
    boxShadow: withFocusStyle && state.isFocused ? focusShadow : "none",
    fontSize: 16,
    minHeight: 42,
    cursor: "text",
    "&:hover": {
      border: withFocusStyle && state.isFocused ? focusBorder : hoverBorder,
    },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "2px 12px",
    flexWrap: "wrap",
  }),
  input: (base: any) => ({
    ...base,
    fontSize: 16,
    margin: 0,
    padding: "2px 0",
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#aaa",
    fontSize: 16,
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 16,
    backgroundColor: state.isFocused ? "rgba(0,123,255,.05)" : "white",
    color: "#333",
    cursor: "pointer",
    padding: "8px 12px",
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: MENU_PORTAL_Z_INDEX,
    boxShadow: "0 2px 8px rgba(0,0,0,.15)",
    borderRadius: 4,
  }),
  menuPortal: menuPortalStyle,
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: "rgba(0,123,255,.12)",
    borderRadius: 3,
    margin: "2px 4px 2px 0",
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    color: "#0056b3",
    fontSize: 13,
    padding: "2px 4px 2px 6px",
  }),
  multiValueRemove: (base: any) => ({
    ...base,
    color: "#0056b3",
    paddingRight: 4,
    "&:hover": {
      backgroundColor: "rgba(0,123,255,.25)",
      color: "#003d80",
    },
  }),
  dropdownIndicator: () => ({ display: "none" }),
  indicatorSeparator: () => ({ display: "none" }),
  clearIndicator: (base: any) => ({
    ...base,
    padding: "0 8px",
    color: "#aaa",
    "&:hover": { color: "#666" },
  }),
});

const createModernSelectStyles = (): any => ({
  control: (base: any, state: any) => ({
    ...base,
    borderRadius: tableTheme.radiusSm,
    backgroundColor: tableTheme.surface,
    border: `1px solid ${
      state.isFocused ? tableTheme.secondaryLight : tableTheme.border
    }`,
    boxShadow: "none",
    fontSize: "0.8125rem",
    minHeight: tableToolbarControlHeight,
    height: tableToolbarControlHeight,
    lineHeight: `${tableToolbarContentHeight}px`,
    cursor: "text",
    "&:hover": {
      borderColor: tableTheme.secondaryLight,
    },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "0 8px",
    flexWrap: "nowrap" as const,
    overflow: "hidden",
  }),
  input: (base: any) => ({
    ...base,
    fontSize: "0.8125rem",
    margin: 0,
    padding: 0,
    color: tableTheme.textPrimary,
  }),
  placeholder: (base: any) => ({
    ...base,
    color: tableTheme.textSecondary,
    fontSize: "0.8125rem",
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: "0.8125rem",
    backgroundColor: state.isFocused
      ? tableTheme.secondarySoft
      : tableTheme.surface,
    color: tableTheme.textPrimary,
    cursor: "pointer",
    padding: "6px 10px",
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: MENU_PORTAL_Z_INDEX,
    boxShadow: "0 4px 12px rgba(0,0,0,.08)",
    borderRadius: tableTheme.radiusSm,
    border: `1px solid ${tableTheme.border}`,
    overflow: "hidden",
  }),
  menuPortal: menuPortalStyle,
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: tableTheme.secondarySoft,
    borderRadius: tableTheme.radiusXs,
    margin: "1px 4px 1px 0",
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    color: tableTheme.secondaryDark,
    fontSize: "0.75rem",
    padding: "1px 4px 1px 6px",
  }),
  multiValueRemove: (base: any) => ({
    ...base,
    color: tableTheme.secondaryDark,
    paddingRight: 4,
    "&:hover": {
      backgroundColor: "rgba(255, 153, 102, 0.25)",
      color: tableTheme.secondaryDark,
    },
  }),
  dropdownIndicator: () => ({ display: "none" }),
  indicatorSeparator: () => ({ display: "none" }),
  clearIndicator: (base: any) => ({
    ...base,
    padding: "0 6px",
    color: tableTheme.textSecondary,
    "&:hover": { color: tableTheme.textPrimary },
  }),
});

const createModernGroupedSelectStyles = (): any => {
  const base = createModernSelectStyles();
  const fillParent = {
    height: "100%",
    minHeight: 0,
    maxHeight: "100%",
  };
  return {
    ...base,
    container: (provided: any) => ({
      ...provided,
      width: "100%",
      ...fillParent,
      flex: "1 1 auto",
      alignSelf: "stretch",
    }),
    control: (provided: any) => ({
      ...provided,
      backgroundColor: tableTheme.surface,
      border: "none",
      boxShadow: "none",
      fontSize: "0.8125rem",
      lineHeight: `${tableToolbarContentHeight}px`,
      cursor: "text",
      alignItems: "center",
      overflow: "hidden",
      boxSizing: "border-box",
      ...tableToolbarGroupCornerRight,
      ...fillParent,
      "&:hover": {
        border: "none",
        boxShadow: "none",
      },
    }),
    valueContainer: (provided: any) => ({
      ...base.valueContainer(provided),
      position: "relative",
      alignItems: "center",
      padding: "0 8px",
      overflow: "hidden",
      ...fillParent,
    }),
    placeholder: (provided: any, state: any) => ({
      ...base.placeholder(provided),
      margin: 0,
      position: "absolute",
      top: "50%",
      left: 8,
      transform: "translateY(-50%)",
      display: state.selectProps.inputValue ? "none" : "block",
    }),
    input: (provided: any) => ({
      ...base.input(provided),
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      alignSelf: "stretch",
      alignItems: "center",
      ...fillParent,
    }),
    menuPortal: menuPortalStyle,
    menu: (provided: any) => ({
      ...base.menu(provided),
      zIndex: MENU_PORTAL_Z_INDEX,
      marginTop: 4,
    }),
  };
};

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  value: string[];
  onChange: (values: string[]) => void;
  appFetch: (url: string, opts?: any) => Promise<any>;
  hideLabel?: boolean;
  withFocusStyle?: boolean;
  clientId?: string;
  labelingType?: string;
  keepInputValue?: boolean;
  variant?: "default" | "modern";
  inGroup?: boolean;
}

const LabelAutocomplete = ({
  value,
  onChange,
  appFetch,
  hideLabel,
  withFocusStyle = true,
  clientId,
  labelingType,
  keepInputValue = false,
  variant = "default",
  inGroup = false,
}: Props) => {
  const isModern = variant === "modern";
  const [inputValue, setInputValue] = useState("");
  const [allLabels, setAllLabels] = useState<LabelOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    hasFetched.current = false;
    setAllLabels([]);
  }, [clientId, labelingType]);

  const loadLabels = useCallback(() => {
    if (hasFetched.current || !clientId || !labelingType) return;

    setIsLoading(true);
    fetchAvailableLabels(appFetch, clientId, labelingType)
      .then((labels) => {
        setAllLabels(labels);
        hasFetched.current = true;
      })
      .catch(() => setAllLabels([]))
      .finally(() => setIsLoading(false));
  }, [appFetch, clientId, labelingType]);

  const query = inputValue.trim().toLowerCase();
  const filteredOptions = query
    ? allLabels.filter((l) => l.label.toLowerCase().includes(query))
    : allLabels;

  const selectedOptions = value.map(
    (v) => allLabels.find((o) => o.value === v) ?? { value: v, label: v },
  );

  const groupedRootStyle =
    isModern && inGroup ? { width: "100%", height: "100%" } : undefined;

  return (
    <div style={groupedRootStyle}>
      {!hideLabel && (
        <label style={{ fontSize: 16, display: "block", fontWeight: 200 }}>
          Labels
        </label>
      )}
      <div
        style={
          isModern && inGroup
            ? { width: "100%", height: "100%" }
            : { marginTop: hideLabel ? 0 : 8 }
        }
      >
        <Select
          isMulti
          options={filteredOptions}
          isLoading={isLoading}
          inputValue={inputValue}
          onInputChange={(val: string, { action }: any) => {
            if (action === "input-change") setInputValue(val);
          }}
          onFocus={loadLabels}
          styles={
            isModern && inGroup
              ? createModernGroupedSelectStyles()
              : isModern
              ? createModernSelectStyles()
              : createSelectStyles(withFocusStyle)
          }
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : undefined
          }
          menuPosition="fixed"
          placeholder={isModern ? "Rechercher..." : "Rechercher des labels..."}
          noOptionsMessage={() =>
            isLoading ? null : "Aucun résultat"
          }
          loadingMessage={() => "Chargement..."}
          value={selectedOptions}
          onChange={(opts: any, actionMeta: any) => {
            onChange(opts ? opts.map((o: any) => o.value) : []);
            if (actionMeta.action === "select-option" && !keepInputValue) {
              setInputValue("");
            }
          }}
          isClearable
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          filterOption={() => true}
          formatOptionLabel={(opt: any, { context }: any) => {
            const isSelected = value.includes(opt.value);
            if (context === "value") {
              return <span>{opt.label}</span>;
            }
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: isSelected
                    ? isModern
                      ? tableTheme.secondarySoft
                      : "rgba(0,123,255,.08)"
                    : "transparent",
                  margin: isModern ? "-6px -10px" : "-8px -12px",
                  padding: isModern ? "6px 10px" : "8px 12px",
                }}
              >
                <span
                  style={{
                    color: isSelected
                      ? isModern
                        ? tableTheme.secondaryDark
                        : "#0056b3"
                      : isModern
                      ? tableTheme.textPrimary
                      : "#333",
                    fontWeight: isSelected ? 500 : 400,
                    fontSize: isModern ? "0.8125rem" : undefined,
                  }}
                >
                  {opt.label}
                </span>
                {isSelected && (
                  <span
                    style={{
                      color: isModern ? tableTheme.secondaryDark : "#007bff",
                      fontWeight: "bold",
                      fontSize: isModern ? 14 : 16,
                      marginLeft: 8,
                    }}
                  >
                    ✓
                  </span>
                )}
              </div>
            );
          }}
        />
      </div>
    </div>
  );
};

export default LabelAutocomplete;
